/**
 * Surface alignment grid — a basis-aligned grid that overlays the selected
 * face plane while object-mode surface operators select or draw a footprint.
 *
 * The grid is mathematically identical to the world-floor grid: it uses
 * the same shared `gridLogDistNode` LOD uniform, the same nested 0.01 /
 * 0.1 / 1 / 10 cell hierarchy, and the same `getDynamicSnapSpacing`
 * cell size. When grid snapping is enabled, surface handlers round
 * basis-(u, v) to integer multiples of `step1`;
 * rendering the same lattice in screen space means visible grid
 * intersections are exactly the snap targets.
 *
 * Differences from the world grid:
 *   - No axis tinting. The basis tangent / bitangent are not world axes,
 *     so painting them red / green would mislead the user.
 *   - **Negative** polygon offset — the surface grid sits on the host
 *     face and must win the coplanar z-fight (the world grid uses
 *     positive offset to lose to scene surfaces).
 *   - Grazing fade keys off the basis normal, fed in via the
 *     `surfaceAlignmentGridNormalNode` uniform.
 */
import {
  abs,
  cameraPosition,
  clamp,
  Fn,
  float,
  floor,
  fract,
  fwidth,
  max,
  min,
  mix,
  positionLocal,
  positionWorld,
  pow,
  smoothstep,
  uniform,
  vec3,
  vec4,
} from "three/tsl";
import * as THREE from "three/webgpu";
import { GRID_EXTENT } from "../units.ts";
import { gridLogDistNode } from "./groundGridMaterial.ts";

/** Negative offsets push the grid TOWARD the camera so it wins coplanar
 *  z-fight against the host face. */
export const SURFACE_ALIGNMENT_GRID_POLYGON_OFFSET_FACTOR = -1;
export const SURFACE_ALIGNMENT_GRID_POLYGON_OFFSET_UNITS = -1;
export const SURFACE_ALIGNMENT_GRID_FINE_OPACITY = 0.04;
export const SURFACE_ALIGNMENT_GRID_MAJOR_OPACITY = 0.1;
export const SURFACE_ALIGNMENT_GRID_COARSE_OPACITY = 0.14;

/**
 * World-space normal of the basis plane the grid currently sits on.
 * Updated each time a new face basis is locked; drives the grazing fade.
 */
export const surfaceAlignmentGridNormalNode = uniform(
  new THREE.Vector3(0, 1, 0),
);
export const surfaceAlignmentGridOffsetNode = uniform(new THREE.Vector2(0, 0));

export function setSurfaceAlignmentGridNormal(
  normal: readonly [number, number, number],
): void {
  (surfaceAlignmentGridNormalNode.value as THREE.Vector3).set(
    normal[0],
    normal[1],
    normal[2],
  );
}

export function setSurfaceAlignmentGridOffset(u: number, v: number): void {
  (surfaceAlignmentGridOffsetNode.value as THREE.Vector2).set(u, v);
}

export function createSurfaceAlignmentGridMaterial(): THREE.NodeMaterial {
  const mat = new THREE.NodeMaterial();
  mat.transparent = true;
  mat.side = THREE.DoubleSide;
  mat.depthWrite = false;
  mat.polygonOffset = true;
  mat.polygonOffsetFactor = SURFACE_ALIGNMENT_GRID_POLYGON_OFFSET_FACTOR;
  mat.polygonOffsetUnits = SURFACE_ALIGNMENT_GRID_POLYGON_OFFSET_UNITS;
  mat.name = "SurfaceAlignmentGridMaterial";

  mat.fragmentNode = Fn(() => {
    // Local XY is relative to the current hit origin. Add the world-anchored
    // basis offset so grid cells stay fixed when a different point on the
    // same surface becomes the drawing origin.
    const pos = positionLocal.xy.add(surfaceAlignmentGridOffsetNode);
    const deriv = fwidth(pos);

    // LOD scheme — identical to the world grid so all grids zoom in
    // step. clamp([-1, 1]) gives three discrete levels:
    //   level -1: 0.01 / 0.1 / 1
    //   level  0: 0.1 / 1   / 10
    //   level  1: 1   / 10  / 100
    const clampedLogDist = clamp(gridLogDistNode.sub(0.5), -1.0, 1.0);
    const level = floor(clampedLogDist);
    const step1 = pow(10, level);
    const step10 = step1.mul(10);
    const step01 = step1.div(10);
    const f = fract(clampedLogDist);

    const gridAt = (stepSize: ReturnType<typeof pow>) => {
      const g = abs(fract(pos.div(stepSize).sub(0.5)).sub(0.5)).div(
        deriv.div(stepSize),
      );
      return float(1.5).sub(min(g.x, g.y)).clamp(0, 1);
    };

    const l01 = gridAt(step01);
    const l1 = gridAt(step1);
    const l10 = gridAt(step10);

    const opacity01 = l01
      .mul(SURFACE_ALIGNMENT_GRID_FINE_OPACITY)
      .mul(float(1.0).sub(f));
    const opacity1 = l1.mul(
      mix(
        float(SURFACE_ALIGNMENT_GRID_MAJOR_OPACITY),
        float(SURFACE_ALIGNMENT_GRID_FINE_OPACITY),
        f,
      ),
    );
    const opacity10 = l10.mul(SURFACE_ALIGNMENT_GRID_COARSE_OPACITY);

    const gridColor = vec3(1, 1, 1);

    // Edge fade — dissolve from 40% of half-extent outward so the lines
    // never hit the geometry boundary at full opacity.
    const dist = max(abs(pos.x), abs(pos.y));
    const fade = float(1.0).sub(
      smoothstep(float((GRID_EXTENT / 2) * 0.1), float(GRID_EXTENT / 2), dist),
    );

    // Grazing fade — sin(elevation) at this fragment relative to the
    // locked basis plane.
    const camToFrag = positionWorld.sub(cameraPosition);
    const camToFragLen = max(camToFrag.length(), float(0.001));
    const grazingFade = smoothstep(
      float(0.08),
      float(0.25),
      abs(surfaceAlignmentGridNormalNode.dot(camToFrag)).div(camToFragLen),
    );

    const finalOpacity = max(max(opacity01, opacity1), opacity10)
      .mul(fade)
      .mul(grazingFade);

    return vec4(gridColor, finalOpacity);
  })();

  return mat;
}

/** Singleton — module-static so every caller shares one TSL compile. */
export const surfaceAlignmentGridMaterial =
  createSurfaceAlignmentGridMaterial();
